import React, { FC, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { setProfile, setToken } from "auth";
import { MagicLinkSession } from "auth/utils/magicLink";
import { UserProfile } from "generated/graphql";
import { State } from "redux/state";

import { FetchProfileComponent } from "components/Authorize/components/FetchProfileComponent";
import { MagicLinkSignIn } from "components/Login/components/MagicLinkSignIn";

/**
 * Route host for passwordless sign-in.
 *
 * Its own route rather than a button on `/sign-up`, for two reasons. `/login`
 * is not a form at all — it bounces straight to the Cognito hosted UI, which
 * cannot host a custom auth flow — and `/sign-up` renders `LoginForm` from
 * `@xapp/components`, which has no slot for another action.
 *
 * Once tokens come back this follows exactly the path the password flow takes:
 * `setToken`, then `FetchProfileComponent` to load the profile, then out to the
 * app. Nothing about being signed in should depend on which flow got you here.
 */
export const MagicLinkSignInContainer: FC = () => {
    const dispatch = useDispatch();
    const credentials = useSelector((state: State) => state.auth?.credentials);
    const profile = useSelector((state: State) => state.auth?.profile);

    const handleSignIn = useCallback(
        (session: MagicLinkSession) => {
            dispatch(setToken(session));
        },
        [dispatch]
    );

    const handleProfile = useCallback(
        (userProfile: UserProfile) => {
            dispatch(setProfile(userProfile));
        },
        [dispatch]
    );

    if (credentials && !profile) {
        return (
            <FetchProfileComponent
                accessToken={credentials.accessToken}
                onError={(e) => {
                    console.log(`error fetching profile ${e}`);
                }}
                onProfile={handleProfile}
            />
        );
    }

    if (credentials && profile) {
        return <Redirect to="/" />;
    }

    return <MagicLinkSignIn onSignIn={handleSignIn} />;
};
